










































































import {
  ITimeOffAttachment,
  ITimeOffAttachmentFieldError,
  TimeOffAttachment
} from '@/models';
import { ErrorManager } from '@/models/error';
import { isURL } from '@/utils/helpers';
import Vue, { PropType } from 'vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';

export type InputMessage = string | string[];
export type InputValidationRule = (value: any) => string | boolean;
export type InputValidationRules = (InputValidationRule | string)[];

export default Vue.extend({
  components: { ConfirmationDialog },
  name: 'TimeOffAttachmentListItem',
  props: {
    attachment: { type: TimeOffAttachment, required: true },
    deleteAttachmentAction: {
      type: Function as PropType<
        (attachment: ITimeOffAttachment) => Promise<void>
      >,
      required: true
    },
    fieldError: {
      type: Object as PropType<ITimeOffAttachmentFieldError>
    }
  },

  data() {
    return {
      fileReader: new FileReader(),
      percentage: 0,
      deleteAttachmentDialog: false,
      deleteErrorMessage: ''
    };
  },

  computed: {
    isLoadingFile(): boolean {
      return this.percentage > 0 && this.percentage < 100;
    },
    internalErrorMessages(): (InputValidationRule | string)[] {
      if (!this.fieldError) return [];
      return this.genInternalMessages(this.fieldError.file);
    }
  },

  mounted() {
    this.addFileListeners();
    this.readasDataURL(this.attachment.file!);
  },

  methods: {
    addFileListeners() {
      this.fileReader.addEventListener('progress', event => {
        if (event.lengthComputable) {
          // we can calculate the percentage
          this.percentage = (event.loaded / event.total) * 100;
        } else if (event.loaded) this.percentage = 100;
      });
    },

    readasDataURL(file: string | File) {
      if (file instanceof File) this.fileReader.readAsDataURL(file);
    },

    getFileName(file: string | File, extension: boolean) {
      const fileName = typeof file === 'string' ? file : file.name;

      let fileUrlName = fileName;

      if (isURL(fileUrlName)) {
        fileUrlName = decodeURIComponent(
          new URL(fileName).pathname.split('/').pop()!
        );
      }

      if (!fileUrlName) return fileName; // some default fileUrlName

      if (extension) return fileUrlName.split('.').pop();

      return fileUrlName;
    },

    removeTimeOffAttachment() {
      this.clearAllErrors();
      if (!this.attachment.id) {
        this.fileReader.abort();
        this.$emit('remove:attachment', this.attachment);
      } else {
        this.deleteAttachmentDialog = true;
      }
    },

    closeAttachmentDialog() {
      this.deleteAttachmentDialog = false;
    },
    confirmAttachmentDialog() {
      this.deleteAttachmentAction(this.attachment)
        .then(() => {
          this.$emit('remove:attachment', this.attachment);
          this.deleteAttachmentDialog = false;
        })
        .catch((error: any) => {
          this.deleteErrorMessage = ErrorManager.extractApiError(error);
          return;
        });
    },

    clearAllErrors(): void {
      this.deleteErrorMessage = '';
    },

    roundedPercentage(num: number) {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    },

    getFileIconColor(extension: string) {
      switch (extension) {
        case 'pdf':
          return 'error';

        case 'doc':
        case 'docx':
          return 'blue darken-1';

        case 'pptx':
        case 'ppt':
          return 'orange';

        case 'xlsx':
        case 'xls':
          return 'success';

        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'primary lighten-3';

        case 'txt':
        default:
          return 'grey';
      }
    },

    getFileIcon(extension: string) {
      switch (extension) {
        case 'pdf':
          return 'mdi-file-pdf-box';

        case 'doc':
        case 'docx':
          return 'mdi-file-word-box';

        case 'pptx':
        case 'ppt':
          return 'mdi-file-powerpoint-box';

        case 'xlsx':
        case 'xls':
          return 'mdi-file-excel-box';

        case 'png':
          return 'mdi-file-png-box';

        case 'jpg':
        case 'jpeg':
          return 'mdi-file-jpg-box';

        case 'txt':
        default:
          return 'mdi-text-box';
      }
    },

    genInternalMessages(messages: InputMessage | null): InputValidationRules {
      if (!messages) return [];
      else if (Array.isArray(messages)) return messages;
      else return [messages];
    }
  }
});
